import { useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { Lock, CheckCircle } from 'lucide-react';
import { formatApiErrorDetail } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full bg-[#121212] border border-[#27272A] focus:border-[#C41E3A] text-white text-sm px-4 py-3 rounded-sm outline-none transition-colors";

export default function ResetPasswordPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = new URLSearchParams(location.search).get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setDone(true);
      setTimeout(() => navigate('/account'), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[60vh] bg-[#050505] py-16 px-4" data-testid="reset-password-page">
      <div className="max-w-md mx-auto bg-[#0A0A0A] border border-[#27272A] rounded-sm p-8">
        <h1 className="font-['Oswald'] text-2xl uppercase tracking-wide text-white text-center mb-6">Choose a New Password</h1>
        {done ? (
          <div className="text-center" data-testid="reset-success">
            <CheckCircle size={40} className="text-green-400 mx-auto mb-3" />
            <p className="text-green-400 text-sm">Password updated! Taking you to sign in...</p>
          </div>
        ) : !token ? (
          <p className="text-[#C41E3A] text-sm text-center">This reset link is invalid. Please request a new one from the sign-in page.</p>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="relative">
              <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input type="password" required minLength={6} placeholder="New password (6+ characters)" value={password} onChange={e => setPassword(e.target.value)} className={`${inputCls} pl-9`} data-testid="reset-password-input" />
            </div>
            <div className="relative">
              <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input type="password" required minLength={6} placeholder="Confirm new password" value={confirm} onChange={e => setConfirm(e.target.value)} className={`${inputCls} pl-9`} data-testid="reset-confirm-input" />
            </div>
            {error && <p className="text-[#C41E3A] text-sm" data-testid="reset-error">{error}</p>}
            <button type="submit" disabled={busy} className="w-full bg-[#C41E3A] hover:bg-[#A01830] disabled:opacity-50 text-white font-bold uppercase tracking-wider py-3.5 rounded-sm transition-colors" data-testid="reset-submit-btn">
              {busy ? 'Please wait...' : 'Update Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
